import React from "react";
import {
  ACODropdown,
  ACODropdownItem,
  CheckCircleIcon,
  Dialog,
  Modal,
} from "staysure-component-library";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import TextSelectionButton from "@/components/atoms/text-selection-button/text-selection-button";
import ToggleBtnLabelWrapper from "@/components/atoms/toggle-btn-wrapper/toggle-btn-label-wrapper";
import CoronaModal from "@/components/molecules/quote-section/acos/corona-modal";
import MoreInfoDrawer from "@/components/molecules/quote-section/acos/more-info-drawer";
import Styles from "@/components/molecules/quote-section/acos/single-aco-section.module.css";
import useMediaQuery from "@/hooks/use-media-query/use-media-query";
import { useAppSelector } from "@/store/hook";
import { RootState } from "@/store/root-reducer";
import singleAcoClassNames from "@/utils/single-aco-section-brand-styles.utils";

const getDrawerContent = (
  code: string,
  drawerContent: string,
  isBeforeGadgetMigration?: boolean
) => {
  if (code === "GADGET_COVER" && !isBeforeGadgetMigration) {
    return drawerContent.replace(/&lt;PLACEHOLDER_GADGET_MIGRATION&gt;/g, "");
  } else if (code === "GADGET_COVER" && isBeforeGadgetMigration) {
    return drawerContent.replace(
      /&lt;PLACEHOLDER_GADGET_MIGRATION&gt;/g,
      "(excludes laptops)"
    );
  }
  return drawerContent;
};

type CoverOptionProps = {
  title: string;
  fullPrice: string;
  monthlyPrice: string;
  totalPrice: string;
  description: string;
  state: string;
  isGolfEquipment?: boolean;
  actionLabel: string;
  code: string;
  action: (code: string, value?: string) => void;
  removeAction: (code: string) => void;
  isSelected: boolean;
  moreInfoLabel: string;
  removeLabel: string;
  chooseLabel: string;
  golfSelectKey: string;
  drawerCloseCta: string;
  drawerContent: string;
  tdeModalTitle?: string;
  tdeModalCopy?: string;
  tdeModalConfirmBtnLabel?: string;
  tdeModalCancelBtnLabel?: string;
};

function PolicyExtrasOption({
  title,
  fullPrice,
  monthlyPrice,
  totalPrice,
  description,
  code,
  isGolfEquipment = false,
  actionLabel,
  isSelected,
  moreInfoLabel,
  action,
  removeAction,
  removeLabel,
  chooseLabel,
  golfSelectKey,
  drawerCloseCta,
  drawerContent,
  tdeModalTitle = "",
  tdeModalCopy = "",
  tdeModalConfirmBtnLabel = "",
  tdeModalCancelBtnLabel = "",
}: Readonly<CoverOptionProps>) {
  const { attributes } = useAppSelector((state: RootState) => state.quote);
  const [isMoreInfoOpen, setIsMoreInfoOpen] = React.useState(false);
  const [isCoronaModalOpen, setIsCoronaModalOpen] = React.useState(false); // State for Corona modal

  const travelers = attributes?.travellerDetails?.traveller || [];
  const isBeforeGadgetMigration =
    attributes?.gadgetMigration?.isBeforeGadgetMigration;
  const isMobile = useMediaQuery("(max-width: 600px)");

  const handleTDEAction = () => {
    const handleUnselectedState = () => {
      setIsCoronaModalOpen(true);
    };

    const handleSelectedState = () => {
      action(code);
    };

    if (!isSelected) {
      handleUnselectedState();
    } else {
      handleSelectedState();
    }
  };

  const handleAction = () => {
    if (code === "TDE") {
      handleTDEAction();
    } else {
      action(code);
    }
  };

  const showPolicyExtraCoverPrices = (
    sellingPrice: string,
    pclPrice: string,
    pclTotal: string
  ) => {
    return (
      <div className="flex flex-col text-text-title w-full mb-spacing-xs">
        <div className="flex">
          {isGolfEquipment && !isSelected && (
            <p className="font-body-font text-[18px] leading-[150%] font-[400]">
              From&nbsp;
            </p>
          )}
          <p className="font-semibold-font text-[18px] leading-[150%] h-[27px] font-[600] items-center">
            {sellingPrice}
          </p>
          {!isGolfEquipment || isSelected ? (
            <p className="font-body-font text-[18px] leading-[150%] font-[400]">
              &nbsp;in full or&nbsp;
            </p>
          ) : null}
        </div>

        {pclPrice && pclTotal && (
          <div className="flex">
            <p className="font-semibold-font text-[18px] leading-[150%] h-[27px] font-[600] items-center">
              {pclPrice}
            </p>
            <p className="font-body-font text-[18px] leading-[150%] font-[400]">
              &nbsp;a month (total {pclTotal})
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* CORONA modal */}
      {isCoronaModalOpen && (
        <Modal
          onOpenChange={(e: boolean) => setIsCoronaModalOpen(e)}
          isOpen={isCoronaModalOpen}
          modalStyles={{
            top: `50%`,
            left: `50%`,
            transform: `translate(-50%, -50%)`,
          }}
          className="bg-surface-neutral-option-1 max-w-[327px] md:max-w-[416px] relative rounded-radius-lg"
          uderlayClassName="z-[100] bg-interactive-overlay-default"
        >
          <Dialog className="relative">
            <CoronaModal
              onSelect={(e) => {
                setIsCoronaModalOpen(false);
                if (e) {
                  action(code);
                }
              }}
              title={tdeModalTitle}
              description={tdeModalCopy}
              confirmCTALabel={tdeModalConfirmBtnLabel}
              rejectCTALabel={tdeModalCancelBtnLabel}
            />
          </Dialog>
        </Modal>
      )}

      {/* more info Drawer */}

      <MoreInfoDrawer
        isDraweOpenOpen={isMoreInfoOpen}
        setIsDraweOpenOpen={(e) => setIsMoreInfoOpen(e)}
        title={title}
        closeCta={drawerCloseCta}
        content={getDrawerContent(code, drawerContent, isBeforeGadgetMigration)}
      />
      <div
        data-testid="SingleACOSection-testid"
        className="flex justify-between self-stretch mb-spacing-xs md:mb-spacing-xxs
       md:min-h-[40px] min-h-[24px] lg:items-center md:items-center items-center"
      >
        <h1
          className={`${singleAcoClassNames.acoHeaderClassName} ${Styles.acoTitle} md:text-[25px] text-[18px] leading-[115%] text-text-functional`}
        >
          {title}
        </h1>
        {!isMobile && (
          <div className="h-[40px] max-h-[40px] md:flex md:flex-col md:items-center lg:block">
            {!isGolfEquipment && (
              <ToggleBtnLabelWrapper
                id={`toggle-button-label-${code}`}
                label={actionLabel}
                btnType="button"
                isActiveRightIcon={false}
                isDefaultActive={isSelected}
                isToggleEnable={false}
                className={`${
                  isSelected ? Styles.acoBtnActive : Styles.acoBtnInactive
                } break-words w-[151px]  px-spacing-m
               min-h-[40px] rounded-compact-radius-pill border-[1.5px] border-interactive-secondary-default
            ${
              isSelected
                ? "!text-dark-text-on-secondary"
                : "!text-interactive-secondary-default"
            } !justify-between items-center
             text-[18px] font-body-font leading-[100%] font-[600] min-h-[40px]
            ${
              isSelected
                ? "!bg-interactive-secondary-default"
                : "!bg-dark-text-on-secondary hover:!bg-interactive-action-hovered"
            }`}
                isLeftIcon
                leftIcon={
                  isSelected ? (
                    <CheckCircleIcon fill="white" stroke="#306582" />
                  ) : (
                    <></>
                  )
                }
                activeLeftIcon={
                  isSelected ? (
                    <CheckCircleIcon fill="white" stroke="#306582" />
                  ) : (
                    <></>
                  )
                }
                onClick={() => handleAction()}
              />
            )}

            {isGolfEquipment && (
              <div className={Styles.acoDropDownWrapper}>
                <ACODropdown
                  data-testid={`aco-dropdown-golf-cover-one-${code}`}
                  id={`aco-dropdown-golf-cover-one-${code}`}
                  isActiveLeftIcon
                  leftActiveIcon={
                    <CheckCircleIcon fill="white" stroke="#306582" />
                  }
                  selectedKey={golfSelectKey ?? "0"}
                  listClassName="mb-[2px] mx-[4px] px-[8px] p-spacing-xxs !bg-transparent !text-text-functional
                    rounded-radius-s hover:!bg-interactive-action-hovered focus:!bg-interactive-action-hovered"
                  unorderedListClassName={` ${
                    golfSelectKey === "0"
                      ? "md:w-[116px] lg:w-[151px]"
                      : "md:w-fit md:min-w-[151px]"
                  } py-[4px] mt-[-2px] border rounded-radius-xxl border-border-subdued 
                  !bg-surface-neutral-option-1 shadow-[0px_0px_12px_0px_rgba(15,23,42,0.11)]`}
                  placeholderText="Choose"
                  className={` ${
                    golfSelectKey !== "0"
                      ? Styles.golfDropdownActive
                      : Styles.golfDropdownInactive
                  } ${
                    golfSelectKey === "0"
                      ? "md:w-[116px] lg:w-[151px]"
                      : "md:w-fit md:min-w-[151px]"
                  } px-[12px]
                    h-[40px] rounded-radius-full border-2 border-interactive-secondary-default
                  ${
                    golfSelectKey !== "0"
                      ? "!text-dark-text-on-secondary"
                      : "!text-interactive-secondary-default"
                  } !justify-between items-center
                  text-[18px] font-body-font leading-[100%] font-[600] 
                  ${
                    golfSelectKey !== "0"
                      ? "!bg-interactive-secondary-default"
                      : "!bg-surface-neutral-option-1"
                  } disabled:cursor-not-allowed disabled:border-border-disabled`}
                  onSelectionChange={(e: string) => action(code, e as string)}
                >
                  {travelers.map((item, index) => {
                    const personOrPersons = index === 0 ? "Person" : "Persons";
                    return (
                      <ACODropdownItem
                        data-testid="ACODropdownItem-testid-1"
                        value={`${index + 1} ${personOrPersons}`}
                        keyValue={`${index + 1}`}
                        key={item.sequence}
                      >
                        {`${index + 1} ${personOrPersons}`}
                      </ACODropdownItem>
                    );
                  })}
                </ACODropdown>
              </div>
            )}

            {isSelected && (
              <div
                className={`w-[151px] ${
                  golfSelectKey !== "0" ? "md:w-[151px]" : "md:w-[116px]"
                } lg:w-[151px] text-text-critical text-[16px] font-body-font font-[600] leading-[19.49px] text-center md:mt-spacing-xs`}
              >
                <LabelButtonWrapper
                  data-testid={`golf-remove-button-${code}`}
                  id={`golf-remove-button-${code}`}
                  hierarchy="tertiary"
                  label={removeLabel ?? "Remove"}
                  className={`${Styles.removeBtn} px-spacing-xxs text-[16px] font-body-font font-[600]  text-text-critical leading-[19.49px]`}
                  onClick={() => removeAction(code)}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className=" max-w-[100%] md:w-[298px]">
        {showPolicyExtraCoverPrices(fullPrice, monthlyPrice, totalPrice)}
        <div className=" max-w-[100%] lg:mb-spacing-xs">
          <RichText
            richText={description}
            className="font-body-font font-[400] leading-[150%] text-[16px] text-text-functional w-[100%]"
          />
        </div>
        <div>
          <TextSelectionButton
            id={`aco-section-${code}`}
            data-testid={`aco-section-${code}`}
            label={moreInfoLabel ?? "More info"}
            className={`${Styles.textSelectionButton} text-[16px] font-body-font font-[600] align-top lg:leading-[19.49px] leading-[150%]
                   hover:text-interactive-secondary-default hover:bg-surface-neutral-option-1 !px-0 `}
            onClick={() => {
              setIsMoreInfoOpen(true);
            }}
          />
        </div>
      </div>

      {isMobile && (
        <div className="h-[40px] max-h-[40px] mt-spacing-xs flex items-center">
          {!isGolfEquipment && (
            <ToggleBtnLabelWrapper
              data-testid={`toggle-button-mobile-${code}`}
              id={`toggle-button-mobile-${code}`}
              label={actionLabel}
              btnType="button"
              isActiveRightIcon={false}
              isDefaultActive={isSelected}
              isToggleEnable={false}
              className={`${
                isSelected ? Styles.acoBtnActive : Styles.acoBtnInactive
              } w-[151px] md:w-[116px] lg:w-[151px] px-[12px]
                     h-[40px] rounded-radius-full border-2 border-interactive-secondary-default
                   ${
                     isSelected
                       ? "!text-dark-text-on-secondary"
                       : "!text-interactive-secondary-default"
                   } !justify-between items-center
                   text-[18px] font-body-font leading-[100%] font-[600]  
                   ${
                     isSelected
                       ? "!bg-interactive-secondary-default"
                       : "!bg-dark-text-on-secondary hover:!bg-interactive-action-hovered"
                   }`}
              isLeftIcon
              leftIcon={
                isSelected ? (
                  <CheckCircleIcon fill="white" stroke="#306582" />
                ) : (
                  <></>
                )
              }
              activeLeftIcon={
                isSelected ? (
                  <CheckCircleIcon fill="white" stroke="#306582" />
                ) : (
                  <></>
                )
              }
              onClick={() => handleAction()}
            />
          )}

          {isGolfEquipment && (
            <div className={Styles.acoDropDownWrapper}>
              <ACODropdown
                data-testid={`aco-dropdown-golf-cover-two-${code}`}
                id={`aco-dropdown-golf-cover-two-${code}`}
                isActiveLeftIcon
                leftActiveIcon={
                  <CheckCircleIcon fill="white" stroke="#306582" />
                }
                selectedKey={golfSelectKey ?? "0"}
                listClassName="mb-[2px] mx-[4px] px-[8px] p-spacing-xxs !bg-transparent !text-text-functional
                          rounded-radius-s hover:!bg-interactive-action-hovered focus:!bg-interactive-action-hovered"
                unorderedListClassName={`${
                  golfSelectKey === "0" ? "w-[151px]" : "w-fit min-w-[151px]"
                } py-[4px] mt-[-2px] border rounded-radius-xxl border-border-subdued
                      !bg-surface-neutral-option-1 shadow-[0px_0px_12px_0px_rgba(15,23,42,0.11)] overflow-hidden`}
                placeholderText={chooseLabel ?? "Choose"}
                className={` ${
                  golfSelectKey !== "0"
                    ? Styles.golfDropdownActive
                    : Styles.golfDropdownInactive
                } ${
                  golfSelectKey === "0" ? "w-[151px]" : "w-fit min-w-[151px]"
                } px-[12px]
                    h-[40px] rounded-radius-full border-2 border-interactive-secondary-default
                  ${
                    golfSelectKey !== "0"
                      ? "!text-dark-text-on-secondary"
                      : "!text-interactive-secondary-default"
                  } !justify-between items-center
                  text-[18px] font-body-font leading-[100%] font-[600] 
                  ${
                    golfSelectKey !== "0"
                      ? "!bg-interactive-secondary-default"
                      : "!bg-surface-neutral-option-1"
                  } disabled:cursor-not-allowed disabled:border-border-disabled`}
                onSelectionChange={(e: string) => action(code, e as string)}
              >
                {travelers.map((item, index) => {
                  const personOrPersons = index === 0 ? "Person" : "Persons";
                  return (
                    <ACODropdownItem
                      data-testid="ACODropdownItem-testid-1"
                      value={`${index + 1} ${personOrPersons}`}
                      keyValue={`${index + 1}`}
                      key={item.sequence}
                    >
                      {`${index + 1} ${personOrPersons}`}
                    </ACODropdownItem>
                  );
                })}
              </ACODropdown>
            </div>
          )}

          {(golfSelectKey !== "0" || isSelected) && (
            <div className="ml-spacing-m text-text-critical text-[16px] font-body-font font-[600] leading-[19.49px] text-center flex items-center">
              <LabelButtonWrapper
                data-testid={`golf-remove-button-${code}`}
                id={`golf-remove-button-${code}`}
                hierarchy="tertiary"
                label={removeLabel ?? "Remove"}
                className={`${Styles.removeBtn} px-spacing-xxs text-[16px] font-body-font font-[600] leading-[19.49px] text-text-critical `}
                onClick={() => {
                  removeAction(code);
                }}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default PolicyExtrasOption;
